import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { config } from '../config'

// Vehicle Types
export interface VehicleType {
  code: string;
  name: string;
  label: string;
  capacity: number;
  weight: number;
  averageSpeed: number;
  eixos: number;
}

export interface CTEStatistics {
  total_ctes?: number
  monthly_ctes?: number
  average_value?: number
  total_value?: number
  total_freight?: number
  average_freight?: number
  pending_ctes?: number
  in_transit_ctes?: number
  delivered_ctes?: number
  cancelled_ctes?: number
  top_routes?: Array<{
    route: string
    volume: number
    revenue: number
    efficiency: number
  }>
  status_distribution?: {
    completed: number
    in_transit: number
    pending: number
  }
  monthly_trend?: Array<{
    month: string
    year: number
    count: number
    total_value: number
    total_freight: number
  }>
}

export interface NFeStatistics {
  total_nfes?: number
  total_value?: number
  total_tax_value?: number
  average_value?: number
  pending_nfes?: number
  authorized_nfes?: number
  cancelled_nfes?: number
  rejected_nfes?: number
  top_senders?: Array<{
    cnpj: string
    name: string
    count: number
    total_value: number
    avg_value: number
  }>
  top_receivers?: Array<{
    cnpj: string
    name: string
    count: number
    total_value: number
    avg_value: number
  }>
  monthly_trend?: Array<{
    month: string
    year: number
    count: number
    total_value: number
    total_freight: number
  }>
}

export interface MonthRange {
  startMonth: number
  endMonth: number
}

export interface PeriodSearchRequest {
  year: number
  month_ranges: number[]  // Lista de meses selecionados [1, 2, 3, ...]
  additional_filters?: Record<string, any>
}

// AI Analysis Response
export interface AIAnalysisResponse {
  success: boolean
  message: string
  data: {
    summary: {
      total_documents: number
      total_freight_value: number
      analysis_date: string
      period: string
    }
    carrier_analysis: Array<{
      name: string
      cnpj: string
      total_documents: number
      total_freight_value: number
      average_freight_cost: number
      efficiency_score: number
      reliability_score: number
      coverage_regions: string[]
      market_share: number
      performance_trend: string
    }>
    regional_analysis: Array<{
      state: string
      total_documents: number
      total_freight_value: number
      average_freight_cost: number
      carrier_diversity: number
      growth_potential: string
    }>
    strategic_insights: Array<{
      category: string
      title: string
      description: string
      impact: string
      confidence: number
      recommendations: string[]
      data_support?: any
    }>
    visualization_data?: {
      charts: any
      kpis: any
      summary_cards: any[]
    }
  }
}

export interface PeriodSearchResponse {
  success: boolean
  data: any[]
  total: number
  filters: {
    year: number
    month_ranges: MonthRange[]
    additional_filters?: Record<string, any>
  }
  error?: string
}

export interface SalesForceAnalysis {
  success?: boolean
  data?: {
    summary: {
      total_forecasts: number
      total_clients: number
      total_orders: number
      timestamp: string
    }
    kpis: {
      total_volume: number
      geographic_coverage: number
      sales_efficiency: number
      conversion_rate: number
      total_forecasts: number
      total_clients: number
      total_orders: number
    }
    product_distribution: {
      totals: Record<string, number>
      percentages: Record<string, number>
      total_volume: number
      top_product: string | null
    }
    geographic_distribution: {
      states: Record<string, number>
      cities: Record<string, number>
      top_states: Array<[string, number]>
      top_cities: Array<[string, number]>
      total_states: number
      total_cities: number
    }
    performance_metrics: {
      conversion_rate: number
      geographic_coverage: number
      client_efficiency: number
      clients_with_forecasts: number
      total_clients: number
    }
    vehicle_analysis: {
      total_sacks: number
      total_pallets: number
      sacks_per_pallet: number
      vehicles_needed: Record<string, {
        quantity: number
        capacity_per_vehicle: number
        total_capacity: number
        utilization_rate: number
      }>
      most_efficient_vehicle: string
      recommended_vehicles: number
    }
    cost_analysis: {
      total_cost: number
      total_volume: number
      avg_cost_per_ton: number
      costs_by_product: Record<string, {
        volume: number
        cost_per_ton: number
        total_cost: number
      }>
    }
    insights: string[]
  }
  timestamp?: string
}

// Business Units interfaces
export interface BusinessUnit {
  id: string
  code: string
  name: string
  active: boolean
  address?: {
    street: string
    number: string
    complement?: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
  }
  contact?: {
    phone?: string
    email?: string
  }
  freight_data?: {
    total_calculations: number
    total_cost: number
    total_distance: number
    total_pallets: number
    total_shipments: number
    avg_cost: number
    avg_distance: number
    min_cost: number
    avg_cost_per_km: number
    vehicles: Array<{
      name: string
      code: string
      quantity: number
      total_cost: number
    }>
  }
}

export interface BusinessUnitsResponse {
  success: boolean
  message: string
  data: {
    units: BusinessUnit[]
    total_units: number
    company: string
    company_name: string
  }
}

export interface ConfigurationData {
  moduleSettings?: {
    cte: boolean
    nfe: boolean
    cd: boolean
    logistics: boolean
    costs: boolean
    clients: boolean
  }
  notificationSettings?: {
    email: boolean
    push: boolean
    sms: boolean
  }
  privacySettings?: {
    analytics: boolean
    cookies: boolean
    dataSharing: boolean
  }
  appearanceSettings?: {
    theme: string
    language: string
    timezone: string
  }
  systemSettings?: {
    autoRefresh: boolean
    refreshInterval: number
    maxDataPoints: number
  }
  metadata?: {
    version: string
    description: string
    lastModifiedBy: string
    lastModifiedAt: string
  }
}

export interface ConfigurationResponse {
  success?: boolean
  data?: ConfigurationData
  source?: 'database' | 'default'
}

export interface SaveConfigurationRequest {
  moduleSettings?: Partial<ConfigurationData['moduleSettings']>
  notificationSettings?: Partial<ConfigurationData['notificationSettings']>
  privacySettings?: Partial<ConfigurationData['privacySettings']>
  appearanceSettings?: Partial<ConfigurationData['appearanceSettings']>
  systemSettings?: Partial<ConfigurationData['systemSettings']>
}

export interface SaveConfigurationResponse {
  success?: boolean
  message?: string
  data?: {
    id: string
    moduleSettings: ConfigurationData['moduleSettings']
    notificationSettings: ConfigurationData['notificationSettings']
    privacySettings: ConfigurationData['privacySettings']
    appearanceSettings: ConfigurationData['appearanceSettings']
    systemSettings: ConfigurationData['systemSettings']
    metadata: ConfigurationData['metadata']
    updatedAt: string
  }
}

export interface DashboardData {
  company_info?: {
    id?: number
    name?: string
    document?: string
    user?: string
  }
  kpis?: {
    total_revenue?: number
    total_ctes?: number
    total_nfes?: number
    active_cds?: number
    total_clients?: number
    active_clients?: number
    client_activity_rate?: number
    total_credit_limit?: number
    monthly_growth?: number
    efficiency_score?: number
    total_routes?: number
    operational_efficiency?: number
    customer_satisfaction?: number
    cost_per_cte?: number
    delivery_on_time?: number
    average_delivery_time?: number
    cost_per_km?: number
    fuel_efficiency?: number
    driver_productivity?: number
    vehicle_utilization?: number
  }
  revenue_analytics?: {
    monthly_trend?: Array<{
      month: string
      revenue: number
      growth: number
    }>
    revenue_by_service?: Array<{
      service: string
      revenue: number
      percentage: number
    }>
    revenue_by_region?: Array<{
      region: string
      revenue: number
      percentage: number
    }>
  }
  cte_analytics?: {
    status_distribution?: {
      completed?: number
      in_transit?: number
      pending?: number
      cancelled?: number
    }
    monthly_volume?: Array<{
      month: string
      volume: number
      value: number
    }>
    top_routes?: Array<{
      route: string
      volume: number
      revenue: number
      efficiency: number
      distance: number
    }>
    cte_by_type?: Array<{
      type: string
      count: number
      percentage: number
    }>
  }
    nfe_analytics?: {
      first_five_nfes_with_occurrence?: Array<{
        nfeNumber: string
        nfeSerie: string
        nfeKey: string
        occurrenceDescription: string
        carrierName: string
      }>
      total_nfe_value?: number
      status_distribution?: {
        authorized?: number
        pending?: number
        cancelled?: number
        rejected?: number
      }
      monthly_volume?: Array<{
        month: string
        volume: number
        value: number
      }>
      top_senders?: Array<{
        cnpj: string
        name: string
        volume: number
        value: number
      }>
      nfe_by_state?: Array<{
        state: string
        volume: number
        percentage: number
      }>
      product_analysis?: {
        top_products?: Array<{
          product: string
          quantity: number
          value: number
          weight: number
          unit_price: number
          category: string
        }>
        category_breakdown?: Array<{
          category: string
          quantity: number
          value: number
          weight: number
          percentage: number
        }>
        weight_analysis?: {
          total_weight?: number
          average_weight_per_nfe?: number
          weight_distribution?: Array<{
            range: string
            count: number
            percentage: number
          }>
        }
        value_analysis?: {
          total_value?: number
          average_value_per_nfe?: number
          value_distribution?: Array<{
            range: string
            count: number
            percentage: number
          }>
        }
      }
    }
  cd_analytics?: {
    cds_by_status?: Array<{
      status: string
      count: number
      percentage: number
    }>
    cds_by_region: Array<{
      region: string
      count: number
      capacity: number
      utilization: number
    }>
    cd_performance: Array<{
      cd: string
      throughput: number
      efficiency: number
      cost_per_unit: number
    }>
  }
  logistics_analytics?: {
    network_coverage?: {
      total_cities?: number
      covered_cities?: number
      coverage_percentage?: number
      new_cities_this_month?: number
      total_states?: number
    }
    cte_performance?: {
      total_ctes_period?: number
      total_value_transported?: number
      average_value_per_cte?: number
      cost_per_cte?: number
      regional_distribution?: Record<string, {
        ctes?: number
        percentage?: number
        value?: number
        avg_cost_per_cte?: number
      }>
      top_regions?: Array<{
        state?: string
        ctes?: number
        percentage?: number
        value?: number
      }>
      strategic_insights?: {
        most_efficient_region?: {
          state?: string
          cost_per_cte?: number
          benchmark?: boolean
        }
        consolidation_opportunities?: Array<{
          state?: string
          current_cost_per_cte?: number
          baseline_cost_per_cte?: number
          premium_percentage?: number
          annual_savings_potential?: number
          opportunity_type?: string
        }>
        growth_opportunities?: Array<{
          state?: string
          current_volume?: number
          current_percentage?: number
          growth_potential_volume?: number
          growth_potential_value?: number
          opportunity_type?: string
        }>
        capacity_analysis?: {
          current_utilization_percentage?: number
          growth_without_capex_percentage?: number
          growth_without_capex_volume?: number
        }
      }
    }
    route_efficiency?: Array<{
      route: string
      distance: number
      avg_time: number
      efficiency: number
      cost: number
    }>
    cost_analysis?: {
      fuel_cost?: number
      maintenance_cost?: number
      driver_cost?: number
      toll_cost?: number
      total_cost?: number
      cost_per_km?: number
      cost_per_cte?: number
    }
  }
  client_analytics?: {
    portfolio_health?: {
      total_clients?: number
      active_clients?: number
      inactive_clients?: number
      activity_rate?: number
      total_credit_limit?: number
      average_credit_limit?: number
      concentration?: {
        top_10_percent_count?: number
        top_10_percent_value?: number
        concentration_percentage?: number
      }
    }
    segmentation?: {
      by_tier?: {
        Gold?: number
        Silver?: number
        Bronze?: number
      }
      gold_details?: {
        count?: number
        percentage?: number
        avg_credit_limit?: number
        active_percentage?: number
        high_potential?: number
      }
      silver_details?: {
        count?: number
        percentage?: number
        avg_credit_limit?: number
        active_percentage?: number
        high_potential?: number
      }
      bronze_details?: {
        count?: number
        percentage?: number
        avg_credit_limit?: number
        active_percentage?: number
        high_potential?: number
      }
    }
    risk_analysis?: {
      at_risk?: {
        count?: number
        percentage?: number
        value_at_risk?: number
      }
      reactivation_opportunity?: {
        count?: number
        percentage?: number
        potential_value?: number
      }
      growth_opportunity?: {
        count?: number
        percentage?: number
        potential_value?: number
      }
    }
    geographic_distribution?: {
      by_state?: Record<string, {
        count?: number
        percentage?: number
        value?: number
      }>
      total_states?: number
      largest_state?: string
      largest_state_percentage?: number
    }
    client_segments?: Array<{
      segment: string
      count: number
      revenue: number
      percentage: number
    }>
    client_satisfaction?: {
      very_satisfied: number
      satisfied: number
      neutral: number
      dissatisfied: number
      very_dissatisfied: number
      nps_score: number
    }
    client_growth?: Array<{
      month: string
      new_clients: number
      churned_clients: number
      net_growth: number
    }>
  }
  performance_metrics?: {
    delivery_performance: {
      on_time_delivery: number
      early_delivery: number
      late_delivery: number
      average_delivery_time: number
      service_level_agreement: number
    }
    operational_efficiency: {
      vehicle_utilization: number
      driver_productivity: number
      fuel_efficiency: number
      maintenance_efficiency: number
      loading_efficiency: number
    }
    quality_metrics: {
      damage_rate: number
      loss_rate: number
      complaint_rate: number
      resolution_time: number
      customer_retention: number
    }
  }
  trends_and_forecasts?: {
    growth_opportunities?: Array<{
      opportunity: string
      potential_revenue: number
      investment: number
      roi: number
    }>
    risk_factors?: Array<{
      risk: string
      probability: number
      impact: string
      mitigation: string
    }>
    market_trends?: Array<{
      trend: string
      impact: string
      growth_rate: number
    }>
  }
  strategic_insights?: {
    growth_opportunities?: Array<{
      opportunity: string
      potential_revenue: number
      investment: number
      roi: number
    }>
    risk_factors?: Array<{
      risk: string
      probability: number
      impact: string
      mitigation: string
    }>
    market_trends?: Array<{
      trend: string
      impact: string
      growth_rate: number
    }>
  }
  alerts?: Array<{
    id: number
    type: string
    priority: string
    title: string
    message: string
    timestamp: string
    action_required: string
  }>
  external_factors?: {
    weather_impact: {
      affected_routes: number
      delayed_deliveries: number
      weather_conditions: string
    }
    traffic_conditions: {
      congested_routes: number
      average_delay: number
      traffic_index: number
    }
    fuel_prices: {
      current_price: number
      price_change: number
      price_trend: string
    }
  }
  transport_analysis?: {
    carrier_efficiency: {
      most_efficient: {
        name: string
        efficiency_score: number
        on_time_delivery: number
        cost_per_km: number
      }
      least_efficient: {
        name: string
        efficiency_score: number
        on_time_delivery: number
        cost_per_km: number
      }
    }
    freight_costs: {
      most_expensive: {
        name: string
        avg_cost_per_km: number
        total_cost: number
        volume: number
      }
      cheapest: {
        name: string
        avg_cost_per_km: number
        total_cost: number
        volume: number
      }
      best_option: {
        name: string
        cost_efficiency_ratio: number
        reliability_score: number
        recommendation: string
      }
    }
    regional_analysis: {
      best_region: {
        name: string
        efficiency_score: number
        avg_cost_per_km: number
        delivery_time: number
        volume: number
      }
      most_expensive_region: {
        name: string
        avg_cost_per_km: number
        delivery_time: number
        volume: number
        reason: string
      }
      regional_breakdown: Array<{
        region: string
        cost_per_km: number
        volume: number
        efficiency: number
      }>
    }
    carrier_comparison: Array<{
      name: string
      efficiency: number
      cost_per_km: number
      on_time: number
      volume: number
      rating: number
    }>
  }
}

// Function to get owner headers with authentication token
const getOwnerAuthHeaders = (): Record<string, string> => {
  try {
    const ownerData = localStorage.getItem('owner_data')
    if (!ownerData) {
      if ((import.meta as any).env?.DEV) {
        console.warn('⚠️ [API] No owner_data found in localStorage')
      }
      return {}
    }

    const owner = JSON.parse(ownerData)

    // Validate owner object structure
    if (!owner || typeof owner !== 'object') {
      if ((import.meta as any).env?.DEV) {
        console.warn('⚠️ [API] Invalid owner_data structure:', owner)
      }
      return {}
    }
    
    // Return headers following audit project pattern with auth token
    const headers: Record<string, string> = {
      'company': owner?.company || '',
      'businessUnit': owner?.businessUnit || '-1',
      'logisticOperator': owner?.logisticOperator || '-1',
      'user': owner?.userName || '',
      'Content-Type': 'application/json'
    }

    // Add authorization header if token exists
    if (owner?.userToken) {
      headers['Authorization'] = `Bearer ${owner.userToken}`
    } else {
      if ((import.meta as any).env?.DEV) {
        console.warn('⚠️ [API] No userToken found in owner_data')
      }
    }

    // Log headers in development (without token for security)
    if ((import.meta as any).env?.DEV) {
      console.log('🔧 [API] Owner headers prepared:', {
        company: headers['company'],
        businessUnit: headers['businessUnit'],
        logisticOperator: headers['logisticOperator'],
        user: headers['user'],
        hasToken: !!headers['Authorization']
      })
    }

    return headers
  } catch (error) {
    // Log error in development
    if ((import.meta as any).env?.DEV) {
      console.error('❌ [API] Error extracting owner headers:', error)
    }
    return {}
  }
}
// Custom baseQuery com tratamento de erro 401
const baseQueryWithAuth = async (args: any, api: any, extraOptions: any) => {
  // Log API call in development
  if ((import.meta as any).env?.DEV) {
    console.log('🌐 [API] Making request:', {
      url: typeof args === 'string' ? args : args.url,
      baseUrl: config.API_BASE_URL,
      fullUrl: typeof args === 'string' 
        ? `${config.API_BASE_URL}${args}` 
        : `${config.API_BASE_URL}${args.url || ''}`
    })
  }

  const baseQuery = fetchBaseQuery({
    baseUrl: config.API_BASE_URL,
    prepareHeaders: async (headers) => {
      try {
        // Get owner headers following audit project pattern
        const ownerHeaders = getOwnerAuthHeaders()

        // If no owner headers found, wait a bit and try again
        if (Object.keys(ownerHeaders).length === 0) {
          if ((import.meta as any).env?.DEV) {
            console.warn('⚠️ [API] No owner headers found, waiting 1s and retrying...')
          }
          await new Promise(resolve => setTimeout(resolve, 1000))
          const retryHeaders = getOwnerAuthHeaders()
          if (Object.keys(retryHeaders).length > 0) {
            Object.entries(retryHeaders).forEach(([key, value]) => {
              if (value) {
                headers.set(key, value)
              }
            })
            if ((import.meta as any).env?.DEV) {
              console.log('✅ [API] Owner headers set after retry')
            }
            return headers
          } else {
            if ((import.meta as any).env?.DEV) {
              console.error('❌ [API] Still no owner headers after retry - request may fail with 401')
            }
          }
        }

        // Set owner headers
        Object.entries(ownerHeaders).forEach(([key, value]) => {
          if (value) {
            headers.set(key, value)
          }
        })

        if ((import.meta as any).env?.DEV && Object.keys(ownerHeaders).length > 0) {
          console.log('✅ [API] Owner headers set successfully')
        }

        return headers
      } catch (error) {
        // Failed to prepare headers, continue with existing
        if ((import.meta as any).env?.DEV) {
          console.error('❌ [API] Error preparing headers:', error)
        }
        return headers
      }
    },
    // Timeout alto: análises de alto volume (CTE/NFE/dashboard) podem levar
    // dezenas de segundos no audit/Mongo de produção
    timeout: 120000,
    fetchFn: async (input, init) => {
      // Log fetch details in development
      if ((import.meta as any).env?.DEV) {
        console.log('📡 [API] Fetch request:', {
          url: typeof input === 'string' ? input : input.url,
          method: init?.method || 'GET',
          hasHeaders: !!init?.headers
        })
      }
      return fetch(input, init)
    },
  })

  const result = await baseQuery(args, api, extraOptions)

  // Log result in development
  if ((import.meta as any).env?.DEV) {
    if (result.error) {
      console.error('❌ [API] Request failed:', {
        status: result.error.status,
        data: result.error.data,
        url: typeof args === 'string' ? args : args.url
      })
    } else {
      console.log('✅ [API] Request successful:', {
        url: typeof args === 'string' ? args : args.url
      })
    }
  }

  // Interceptar erro 401 - Owner não autorizado
  if (result.error && result.error.status === 401) {
    // Disparar evento customizado para o ProtectedRoute capturar
    const errorData = result.error.data as any
    if ((import.meta as any).env?.DEV) {
      console.error('🔒 [API] 401 Unauthorized:', {
        message: errorData?.message || 'Owner authentication required',
        path: window.location.pathname,
        errorData
      })
    }
    window.dispatchEvent(new CustomEvent('auth:unauthorized', {
      detail: {
        message: errorData?.message || 'Owner authentication required',
        path: window.location.pathname
      }
    }))
  }

  return result
}

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['CTE', 'NFe', 'Dashboard', 'CD', 'Logistics', 'Client', 'SalesForce', 'Configuration', 'BusinessUnits', 'Freight', 'VehicleFreight'],
  endpoints: (builder) => ({
    // Dashboard endpoints
    getDashboardOverview: builder.query<DashboardData, { year?: number; months?: number[] } | void>({
      query: (params) => {
        if (!params || (!params.year && !params.months)) {
          return config.ENDPOINTS.DASHBOARD
        }

        const queryParams = new URLSearchParams()
        if (params.year) {
          queryParams.append('year', params.year.toString())
        }
        if (params.months && params.months.length > 0) {
          queryParams.append('months', params.months.join(','))
        }

        return `${config.ENDPOINTS.DASHBOARD}?${queryParams.toString()}`
      },
      providesTags: (_result, _error, arg) => {
        // Create a unique tag based on parameters to enable granular cache invalidation
        const cacheKey = arg && typeof arg === 'object'
          ? `Dashboard-${arg.year || 'all'}-${arg.months?.join('-') || 'all'}`
          : 'Dashboard'
        return [{ type: 'Dashboard' as const, id: cacheKey }]
      },
      // Keep data fresh for 5 minutes
      keepUnusedDataFor: 300,
    }),
    
    getDashboardKPIs: builder.query<Record<string, any>, void>({
      query: () => '/dashboard/kpis',
      providesTags: ['Dashboard'],
    }),
    
    getDashboardTrends: builder.query<Record<string, any>, void>({
      query: () => '/dashboard/trends',
      providesTags: ['Dashboard'],
    }),
    
    getDashboardAlerts: builder.query<Record<string, any>, void>({
      query: () => '/dashboard/alerts',
      providesTags: ['Dashboard'],
    }),
    
    // CTE endpoints
    getCTEStatistics: builder.query<CTEStatistics, Record<string, any>>({
      query: (params) => ({
        url: '/cte/statistics',
        params,
      }),
      providesTags: ['CTE'],
    }),
    
    getCTEs: builder.query<Array<any>, Record<string, any>>({
      query: (params) => ({
        url: '/ctes',
        params,
      }),
      providesTags: ['CTE'],
    }),
    
    getCTEById: builder.query<any, string>({
      query: (id) => `/ctes/${id}`,
      providesTags: ['CTE'],
    }),
    
    getCTEsByStatus: builder.query<Array<any>, string>({
      query: (status) => `/ctes/status/${status}`,
      providesTags: ['CTE'],
    }),
    
    getCTEsByRoute: builder.query<Array<any>, { origin: string; destination: string }>({
      query: ({ origin, destination }) => `/ctes/route/${origin}/${destination}`,
      providesTags: ['CTE'],
    }),
    
    // CTE AI Analysis
    analyzeCTEByPeriod: builder.mutation<AIAnalysisResponse, PeriodSearchRequest>({
      query: (body) => ({
        url: '/cte/ai-analysis',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['CTE'],
    }),

    // CTE AI Analysis Query (similar to getDashboardOverview pattern)
    getCTEAIAnalysis: builder.query<AIAnalysisResponse, { year: number; months: number[] } | undefined>({
      query: (params) => {
        if (!params || (!params.year && !params.months?.length)) {
          return '/cte/ai-analysis'
        }

        // Converter months para monthRanges para a requisição
        const monthRanges = params.months.map(month => ({
          startMonth: month,
          endMonth: month
        }))

        return {
          url: '/cte/ai-analysis',
          method: 'POST',
          body: {
            year: params.year,
            month_ranges: monthRanges,
            additional_filters: {}
          }
        }
      },
      providesTags: ['CTE'],
      // Keep data fresh for 5 minutes (same as dashboard)
      keepUnusedDataFor: 300,
    }),

    // NFe endpoints
    getNFeStatistics: builder.query<NFeStatistics, Record<string, any>>({
      query: (params) => ({
        url: '/nfes/statistics',
        params,
      }),
      providesTags: ['NFe'],
    }),
    
    getNFes: builder.query<Array<any>, Record<string, any>>({
      query: (params) => ({
        url: '/nfes',
        params,
      }),
      providesTags: ['NFe'],
    }),
    
    getNFeById: builder.query<any, string>({
      query: (id) => `/nfes/${id}`,
      providesTags: ['NFe'],
    }),
    
    getNFesByStatus: builder.query<Array<any>, string>({
      query: (status) => `/nfes/status/${status}`,
      providesTags: ['NFe'],
    }),
    
    getNFesBySender: builder.query<Array<any>, string>({
      query: (cnpj) => `/nfes/sender/${cnpj}`,
      providesTags: ['NFe'],
    }),
    
    getNFesByReceiver: builder.query<Array<any>, string>({
      query: (cnpj) => `/nfes/receiver/${cnpj}`,
      providesTags: ['NFe'],
    }),
    
    // NFe Virtual AI Analysis
    analyzeNFeVirtualByPeriod: builder.mutation<AIAnalysisResponse, PeriodSearchRequest>({
      query: (body) => ({
        url: '/nfe/ai-analysis',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NFe'],
    }),
    
    // Freight AI Analysis
    analyzeFreightByPeriod: builder.mutation<AIAnalysisResponse, PeriodSearchRequest>({
      query: (body) => ({
        url: '/freight/ai-analysis',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Freight'],
    }),
    
    // Vehicle Freight AI Analysis
    analyzeVehicleFreightByPeriod: builder.mutation<AIAnalysisResponse, PeriodSearchRequest>({
      query: (body) => ({
        url: '/vehicle-freight/ai-analysis',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['VehicleFreight'],
    }),

    // File Upload endpoints
    uploadDataForAnalysis: builder.mutation<{ success: boolean; message: string; fileId: string }, FormData>({
      query: (formData) => ({
        url: '/logistics/upload-data-for-analysis',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Freight', 'VehicleFreight'],
    }),

    uploadClientsPortfolio: builder.mutation<{ success: boolean; message: string; importedCount: number }, FormData>({
      query: (formData) => ({
        url: '/clients/upload-portfolio',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Client'],
    }),

    // Distribution Center endpoints
    getCDStatistics: builder.query<Record<string, any>, Record<string, any>>({
      query: (params) => ({
        url: '/distribution-centers/statistics',
        params,
      }),
      providesTags: ['CD'],
    }),
    
    getCDs: builder.query<Array<any>, Record<string, any>>({
      query: (params) => ({
        url: '/distribution-centers',
        params,
      }),
      providesTags: ['CD'],
    }),
    
    getCDById: builder.query<any, string>({
      query: (id) => `/distribution-centers/${id}`,
      providesTags: ['CD'],
    }),
    
    getCDsByStatus: builder.query<Array<any>, string>({
      query: (status) => `/distribution-centers/status/${status}`,
      providesTags: ['CD'],
    }),
    
    getCDsByState: builder.query<Array<any>, string>({
      query: (state) => `/distribution-centers/state/${state}`,
      providesTags: ['CD'],
    }),
    
    getCDsByCity: builder.query<Array<any>, string>({
      query: (city) => `/distribution-centers/city/${city}`,
      providesTags: ['CD'],
    }),
    
    // Logistics endpoints
    getLogisticsStatistics: builder.query<Record<string, any>, Record<string, any>>({
      query: (params) => ({
        url: '/logistics/statistics',
        params,
      }),
      providesTags: ['Logistics'],
    }),
    
    getLogisticsNetwork: builder.query<Record<string, any>, void>({
      query: () => '/logistics/network',
      providesTags: ['Logistics'],
    }),
    
    getLogisticsRoutes: builder.query<Record<string, any>, void>({
      query: () => '/logistics/routes',
      providesTags: ['Logistics'],
    }),
    
    getLogisticsCosts: builder.query<Record<string, any>, void>({
      query: () => '/logistics/costs',
      providesTags: ['Logistics'],
    }),
    
    // Client endpoints
    getClientStatistics: builder.query<Record<string, any>, void>({
      query: () => '/client/statistics',
      providesTags: ['Client'],
    }),
    
    getClients: builder.query<Array<any>, Record<string, any>>({
      query: (params) => ({
        url: '/client',
        params,
      }),
      providesTags: ['Client'],
    }),
    
    getClientById: builder.query<any, string>({
      query: (id) => `/client/${id}`,
      providesTags: ['Client'],
    }),
    
    getClientPortfolio: builder.query<Record<string, any>, void>({
      query: () => '/client/portfolio',
      providesTags: ['Client'],
    }),
    
    // Sales Force Analysis endpoints
    getSalesForceAnalysis: builder.mutation<SalesForceAnalysis, PeriodSearchRequest>({
      query: (periodRequest) => ({
        url: config.ENDPOINTS.SALES_FORCE_ANALYSIS,
        method: 'POST',
        body: {
          year: periodRequest.year,
          month_ranges: periodRequest.month_ranges
        },
      }),
      invalidatesTags: ['SalesForce'],
    }),

    // Business Units
    getBusinessUnits: builder.query<BusinessUnitsResponse, void>({
      query: () => ({
        url: '/business-units',
        method: 'GET',
      }),
      providesTags: ['BusinessUnits'],
    }),

  // Configuration endpoints
  getConfiguration: builder.query<ConfigurationResponse, void>({
    query: () => '/configuration/settings',
    providesTags: ['Configuration'],
  }),

  getVehicleTypes: builder.query<VehicleType[], void>({
    query: () => '/configuration/vehicle-types',
    providesTags: ['Configuration'],
  }),

  saveConfigurationNew: builder.mutation<{ success: boolean; data: ConfigurationData; message: string }, ConfigurationData>({
    query: (configurationData) => ({
      url: '/configuration/settings',
      method: 'POST',
      body: configurationData,
    }),
    invalidatesTags: ['Configuration'],
  }),

    updateModuleSettings: builder.mutation<SaveConfigurationResponse, ConfigurationData['moduleSettings']>({
      query: (moduleSettings) => ({
        url: '/configuration/modules',
        method: 'PUT',
        body: moduleSettings,
      }),
      invalidatesTags: ['Configuration'],
    }),

    updateAppearanceSettings: builder.mutation<SaveConfigurationResponse, ConfigurationData['appearanceSettings']>({
      query: (appearanceSettings) => ({
        url: '/configuration/appearance',
        method: 'PUT',
        body: appearanceSettings,
      }),
      invalidatesTags: ['Configuration'],
    }),
  }),
})

export const {
  // Dashboard hooks
  useGetDashboardOverviewQuery,
  useGetDashboardKPIsQuery,
  useGetDashboardTrendsQuery,
  useGetDashboardAlertsQuery,
  
  // CTE hooks
  useGetCTEStatisticsQuery,
  useGetCTEsQuery,
  useGetCTEByIdQuery,
  useGetCTEsByStatusQuery,
  useGetCTEsByRouteQuery,
  useGetCTEAIAnalysisQuery,
  useAnalyzeCTEByPeriodMutation,
  useAnalyzeNFeVirtualByPeriodMutation,
  
  // Freight hooks
  useAnalyzeFreightByPeriodMutation,
  
  // Vehicle Freight hooks
  useAnalyzeVehicleFreightByPeriodMutation,

  // File Upload hooks
  useUploadDataForAnalysisMutation,
  useUploadClientsPortfolioMutation,

  // Distribution Center hooks
  useGetCDStatisticsQuery,
  useGetCDsQuery,
  useGetCDByIdQuery,
  useGetCDsByStatusQuery,
  useGetCDsByStateQuery,
  useGetCDsByCityQuery,
  
  // Logistics hooks
  useGetLogisticsStatisticsQuery,
  useGetLogisticsNetworkQuery,
  useGetLogisticsRoutesQuery,
  useGetLogisticsCostsQuery,
  
  // Client hooks
  useGetClientStatisticsQuery,
  useGetClientsQuery,
  useGetClientByIdQuery,
  useGetClientPortfolioQuery,
  
  // Sales Force hooks
  useGetSalesForceAnalysisMutation,

  // Business Units hooks
  useGetBusinessUnitsQuery,

  // Configuration hooks
  useGetConfigurationQuery,
  useSaveConfigurationNewMutation,
  useUpdateModuleSettingsMutation,
  useUpdateAppearanceSettingsMutation,
} = api

// Configuration Data Interface
export interface ConfigurationData {
  id?: string
  ai_query_limit: number
  forecast_period_months: number
  min_data_points: number
  cache_ttl_seconds: number
  batch_size: number
  chart_refresh_interval: number
  max_insights_display: number
  business_settings?: {
    sacos_por_palete: number
    peso_por_saco: number
    km_max_route: number
    media_sacos_por_order: number
  }
  job_settings?: {
    usa_processamento_customizado_calculo_frete: boolean
    vehicle_type_for_job: string
    calc_freight_veiculo_job: string
  }
  module_settings?: {
    cte: boolean
    nfe: boolean
    cd: boolean
    logistics: boolean
    costs: boolean
    clients: boolean
  }
  active: boolean
}

// Configuration endpoints
export const configurationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getConfiguration: builder.query<{ success: boolean; data: ConfigurationData; message: string }, void>({
      query: () => '/configuration/settings',
      providesTags: ['Configuration'],
    }),
    updateConfiguration: builder.mutation<{ success: boolean; data: ConfigurationData; message: string }, Partial<ConfigurationData> & { reset?: boolean }>({
      query: (data) => ({
        url: data.reset ? '/configuration/settings/reset' : '/configuration/settings',
        method: data.reset ? 'POST' : 'PUT',
        body: data,
      }),
      invalidatesTags: ['Configuration'],
    }),
  }),
})

export const {
  useGetConfigurationQuery: useGetConfigurationQueryNew,
  useUpdateConfigurationMutation: useUpdateConfigurationMutationNew,
  useGetVehicleTypesQuery,
} = configurationApi
